import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import type { CalculationInfoResponse } from '@kk/api/modules/customerPortal';
import { ContractLoanTypeEnum } from '@kk/api/modules/customerPortal';
import { formatDateTime } from '@kk/shared/utils/date';
import { formatCurrency } from '@kk/shared/utils/numbers';
import { scrollToWithCallback } from '@kk/shared/utils/scroll';
import Button from '@kk/ui/components/Button';
import {
  AmortListTable,
  LoanInfo,
  PriceInfo,
} from '@kk/ui/components/Calculator';
import Icon from '@kk/ui/components/Icon';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

export type CalculationWithTitle = CalculationInfoResponse & {
  title: string;
};

interface CalculationResultsProps {
  calculations: CalculationWithTitle[];
}

export function CalculationResults(props: CalculationResultsProps) {
  const { calculations } = props;

  const handlePrint = () => {
    window.print();
  };

  return (
    <section className="grid p-6 panel gap-y-6 print:p-0">
      <header className="flex items-center justify-between print:hidden">
        <h3 className="typ-headline-lg">Beregningsresultater</h3>
        <Button
          theme="secondary"
          onClick={handlePrint}
          label="Udskriv"
          icon="document"
        />
      </header>
      <div className="flex flex-col gap-y-4">
        {calculations.map((calculation, index) => {
          if (calculations.length === 1) {
            return (
              <article
                key={calculation.calculationPath}
                className="print:border-t border-blue-5 print:pt-8"
              >
                {/* Print-only calculation header */}
                <header className="hidden print:block mb-6">
                  <h3 className="typ-headline-lg">{calculation.title}</h3>
                </header>
                <CalculationResult calculation={calculation} />
              </article>
            );
          }
          return (
            <ResultDisclosure
              key={calculation.calculationPath}
              calculation={calculation}
              index={index}
              totalCount={calculations.length}
            />
          );
        })}
      </div>
    </section>
  );
}

function ResultDisclosure(props: {
  calculation: CalculationWithTitle;
  index: number;
  totalCount: number;
}) {
  const { calculation } = props;
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Auto-open and scroll to the latest calculation (last in sorted list)
  useEffect(() => {
    if (props.index === props.totalCount - 1) {
      window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (panelRef.current) {
            const elementTop = panelRef.current.getBoundingClientRect().top;
            const offsetPosition = elementTop + window.pageYOffset - 130;

            scrollToWithCallback(
              {
                top: offsetPosition,
                behavior: 'smooth',
              },
              () => {
                buttonRef.current?.focus();
              },
            );
          }
        });
      }, 100);
    }
  }, [props.index, props.totalCount]);

  return (
    <Disclosure defaultOpen={props.index === props.totalCount - 1}>
      {({ open }) => (
        <article
          className={clsx('shadow-md-dl-8 rounded-md print:shadow-none', {
            'print:print-page-break-before': props.index > 0,
          })}
          data-calculation-path={calculation.calculationPath || undefined}
        >
          <header className="print:hidden">
            <DisclosureButton
              ref={buttonRef}
              className={clsx(
                'space-between flex h-16 w-full grid-cols-5 border transition-colors md:grid',
                {
                  'bg-blue-10 border-blue-20 mb-0 rounded-t-md': open,
                  'border-neutral-10 rounded-md': !open,
                },
              )}
              data-calculation-path={calculation.calculationPath || undefined}
            >
              <div className="col-span-2 flex flex-1 items-center space-x-4 px-4 md:flex-auto">
                <Icon type={open ? 'minus' : 'plus'} className="text-blue-60" />
                <div className="flex flex-col items-start">
                  <h4>{calculation.title}</h4>
                  <span className="typ-body-xs text-neutral-100">
                    {calculation.currency}{' '}
                    {formatCurrency(calculation.principalAmount)},{' '}
                    {calculation.loanDuration?.years} år,{' '}
                    {calculation.loanProduct}
                  </span>
                </div>
              </div>
            </DisclosureButton>
          </header>
          {/* Print-only header */}
          <header className="hidden print:block my-6">
            <h4 className="typ-headline-lg">{calculation.title}</h4>
          </header>
          <DisclosurePanel
            ref={panelRef}
            static
            className={clsx('p-4 md:p-6 print:block! print:p-0', {
              'screen:border-blue-20 screen:border-x screen:border-b screen:rounded-b-md':
                open,
              hidden: !open,
            })}
          >
            <CalculationResult calculation={calculation} />
          </DisclosurePanel>
        </article>
      )}
    </Disclosure>
  );
}

function CalculationResult(props: { calculation: CalculationInfoResponse }) {
  const { calculation } = props;

  return (
    <article className="@container grid gap-4">
      <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-4 print:gap-6 print:mb-4 print:grid-cols-1">
        <LoanInfo calculation={calculation} />
        <PriceInfo calculation={calculation} />
      </div>
      {calculation.loanProduct !== ContractLoanTypeEnum.Byggekredit ? (
        <div className="print:mb-4">
          <AmortListTable
            data={calculation.amortList}
            loanProduct={calculation.loanProduct}
            hideFirstRow
          />
        </div>
      ) : null}
      <p className="typ-body-xs text-right text-neutral-100 mb-2 print:block print:clear-both">
        Beregnet:{' '}
        {calculation.calculationDateTime
          ? formatDateTime(calculation.calculationDateTime)
          : 'N/A'}
      </p>
    </article>
  );
}
